import React, { Suspense, lazy } from "react";
import { withRouter, Redirect, Switch, Route } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";

/* loader component for Suspense*/
import PageLoader from "./components/Common/PageLoader";

import Base from "./components/Layout/Base";
import BasePage from "./components/Layout/BasePage";
import { WelfareBootScreen } from "./navigation-v2/react/WelfareShellBootstrapSkeleton";
import LegacyEntryGate from "./navigation-v2/react/LegacyEntryGate";
// Routes d'entités V2 : expressions `import()` partagées avec le préchargement
// au repos (un seul chunk par route). Module sans import statique.
import { welfareEntityRouteLoaders } from "./navigation-v2/app/welfareEntityRouteLoaders";
import DemoModeRemountBoundary from "./components/Layout/DemoModeRemountBoundary";
import DefaultRedirect from "./components/Routes/DefaultRedirect";
// Garde de rôle des routes Classique d'administration et des outils AKONGO
// (même critère que le menu et la Nouvelle : legacyAccessAdapter).
import LegacyRouteGuard from "./components/Routes/LegacyRouteGuard";
import LegacyAuthenticationGate from "./components/Routes/LegacyAuthenticationGate";
import { createEntityRouteRenderer, createStableRouteRenderer } from "./components/Routes/createStableRouteRenderer";
import { ReturnMemoryRecorder } from "./navigation-v2/app/useReturnMemory";
// import BaseHorizontal from './components/Layout/BaseHorizontal';

/* Used to render a lazy component with react-router */
const waitFor = createStableRouteRenderer();
// Vues V1 de fiche : remontees quand l'identifiant d'entite de l'URL change
// (recherche globale, liens internes), sinon la page reste figee sur
// l'ancienne entite : ces vues ne lisent leur identifiant qu'au montage.
const waitForEntity = createEntityRouteRenderer();

// Shell et squelette V2 charges a la demande : importes statiquement, ils
// tiraient `navigation-v2` + `design-system/v2` (~280 Ko gz) dans le chunk
// principal de TOUTES les pages, V1 comprise, et prechargaient les chunks du
// header V2 des la page de login. Decision du 2026-09-05.
const StandaloneProductBoundary = lazy(() => import("./product-access/StandaloneProductBoundary"));
const WelfareProductBoundary = lazy(() => import("./product-access/WelfareProductBoundary"));
const PersistentWelfareShellHost = lazy(() =>
  import("./navigation-v2/react/PersistentWelfareShellHost")
);
const WelfareRouteLoaderChunk = lazy(() =>
  import("./navigation-v2/react/WelfareRouteLoader")
);
const WelfareRouteLoader = () => (
  <Suspense fallback={<PageLoader />}>
    <WelfareRouteLoaderChunk />
  </Suspense>
);

const SingleView = lazy(() => import("./components/SingleView/SingleView"));
const SubMenu = lazy(() => import("./components/SubMenu/SubMenu"));

// CUSTOM VIEW

const testView = lazy(() => import("./components/Views/TestViews"));
const TestSetupEnrichView = lazy(() => import("./components/Views/TestSetupEnrichView"));
const specieAnimalsListView = lazy(() =>
  import("./components/Views/AnimalManagement/SpecieAnimalListView")
);

const AssesmentView = lazy(() => import("./components/Views/AssesmentView"));
const animalManagementView = lazy(() =>
  import("./components/Views/AnimalManagement/AnimalManagementView")
);
const AnimalStatView = lazy(() =>
  import("./components/Views/AnimalManagement/AnimalStatsView")
);
const GroupListView = lazy(() =>
  import("./components/Views/AnimalManagement/GroupListView")
);

const SocialView = lazy(() =>
  import("./components/Views/SocialManagement/SocialView")
);

const SpecieListManagement = lazy(() =>
  import("./components/Views/SpecieManagement/SpecieListManagementView")
);
const SpecieManagementView = lazy(() =>
  import("./components/Views/SpecieManagement/SpecieManagementView")
);
const SpecieQA = lazy(() =>
  import("./components/Views/SpecieManagement/SpecieQA")
);
const IUCNSpeciesSearch = lazy(() =>
  import("./components/Views/SpecieManagement/IUCNSpeciesSearch")
);
const IUCNSpecieForm = lazy(() =>
  import("./components/Views/SpecieManagement/IUCNSpecieForm")
);
const IUCNMigrationTool = lazy(() =>
  import("./components/Views/SpecieManagement/IUCNMigrationTool")
);

const FamilyListManagement = lazy(() =>
  import("./components/Views/FamilyListManagementView")
);
const OrderListManagement = lazy(() =>
  import("./components/Views/OrderListManagementView")
);

const ReportListView = lazy(() =>
  import("./components/Views/reports/reportListView")
);
const ReportManagementView = lazy(() =>
  import("./components/Views/reports/ReportManagementView")
);

const EnclosureListView = lazy(() =>
  import("./components/Views/EnclosureManagement/EnclosureListView")
);
const EnclosureManagementView = lazy(() =>
  import("./components/Views/EnclosureManagement/EnclosureManagementView")
);
const EnclosureSensorView = lazy(() =>
  import("./components/Views/EnclosureManagement/EnclosureSensorView")
);

// const EnrichListView_old = lazy(() =>
//   import("./components/Views/Enrichissements/EnrichListView/EnrichListView_old")
// );
const EnrichManagementView = lazy(() =>
  import("./components/Views/Enrichissements/EnrichView/EnrichView")
);

const EnrichListView = lazy(() =>
  import("./components/Views/Enrichissements/EnrichListView/EnrichListView")
);

const EnrichmentGuidesView = lazy(() =>
  import("./components/Views/EnrichmentGuides/GuideLibraryView")
);
const EnrichmentGuideDetailView = lazy(() =>
  import("./components/Views/EnrichmentGuides/GuideDetailView")
);
const EnrichmentGuideEditorView = lazy(() =>
  import("./components/Views/EnrichmentGuides/GuideEditorView")
);

const EnrichListHistory = lazy(() =>
  import(
    "./components/Views/Enrichissements/EnrichListHistory/EnrichListHistory"
  )
);

const EnrichmentAnalysisListView = lazy(() =>
  import(
    "./components/Views/Enrichissements/EnrichmentAnalysis/EnrichmentAnalysisListView"
  )
);
const EnrichmentAnalysisSelectorView = lazy(() =>
  import(
    "./components/Views/Enrichissements/EnrichmentAnalysis/EnrichmentAnalysisSelectorView"
  )
);

const EnrichCalendarView = lazy(() =>
  import("./components/Views/Enrichissements/EnrichCalendar/EnrichCalendar")
);

const GroupManagementView = lazy(() =>
  import(
    "./components/Views/SiteManagement/GroupManagement/GroupManagementView"
  )
);
const CampaignCreationView = lazy(() =>
  import("./components/Views/CampaignManagement/CampaignCreationView")
);

const CampaignCreationViewOld = lazy(() =>
  import("./components/Views/CampaignManagement/CampaignCreationViewOld")
);

const campaignListView = lazy(() =>
  import(
    "./components/Views/CampaignManagement/CampaignListView/CampaignListView"
  )
);
const CampaignManagementView = lazy(() =>
  import(
    "./components/Views/CampaignManagement/CampaignManagementView/CampaignManagementView"
  )
);

const CampaignQuizzMonitoringView = lazy(() =>
  import(
    "./components/Views/CampaignManagement/CampaignQuizzMonitoring/CampaignQuizzMonitoringView"
  )
);

const userCreation = lazy(() =>
  import("./components/Views/UsersManagement/userCreation")
);
const userSettings = lazy(() =>
  import("./components/Views/UsersManagement/userSetting")
);

const CirManagementView = lazy(() =>
  import("./components/Views/CIR/cirManagementView")
);

const ResearchView = lazy(() =>
  import(
    "./components/Views/research/researchManagementView/researchManagementView"
  )
);
const ResearchListView = lazy(() =>
  import("./components/Views/research/researchListView/researchListView")
);

const QuestionManagementView = lazy(() =>
  import(
    "./components/Views/Quizz/QuestionManagementView/QuestionManagementView"
  )
);
const QuestionListView = lazy(() =>
  import("./components/Views/Quizz/QuestionListView/QuestionListView")
);
const QuestionShortManagement = lazy(() =>
  import("./components/Views/Quizz/QuestionListView/QuestionShortManagement")
);

const ObsListView = lazy(() =>
  import("./components/Views/ObsManagement/ObsListView/ObsListView")
);
const ObsManagementView = lazy(() =>
  import("./components/Views/ObsManagement/ObsManagementView/ObsManagementView")
);
const ObsResultView = lazy(() =>
  import("./components/Views/ObsManagement/ObsResultView/ObsResultView")
);
const ObsPreview = lazy(() =>
  import("./components/Views/ObsManagement/ObsPreview/ObsPreview")
);
const ObsSessionListView = lazy(() =>
  import(
    "./components/Views/ObsManagement/ObsSessionListView/ObsSessionListView"
  )
);
const ObsSessionView = lazy(() =>
  import("./components/Views/ObsManagement/ObsSessionListView/ObsSessionView")
);
const ObsComparisonView = lazy(() =>
  import("./components/Views/ObsManagement/ObsComparisonView/ObsComparisonView")
);
const ObsContextView = lazy(() =>
  import("./components/Views/ObsManagement/ObsContext/ObsContextView")
);
const ObsContextListView = lazy(() =>
  import("./components/Views/ObsManagement/ObsContext/ObsContextListView")
);
const InterObsView = lazy(() =>
  import("./components/Views/ObsManagement/ObsInterUser/InterObsView")
);
const ObsTrainingListView = lazy(() =>
  import("./components/Views/ObsTraining/ObsTrainingListView")
);
// Grille d'évaluation rapide vierge (retour 8, GitLab #1196), interface Classique.
const BlankGridView = lazy(() =>
  import("./components/Views/FastEval/BlankGridView")
);
const ObsTrainingSessionView = lazy(() =>
  import("./components/Views/ObsTraining/ObsTrainingSessionView")
);
const ObsTrainingVideoDetailView = lazy(() =>
  import("./components/Views/ObsTraining/ObsTrainingVideoDetailView")
);
const obsListViewReadOnly = lazy(() =>
  import(
    "./components/Views/ObsManagement/ObsListViewReadOnly/ObsListViewReadOnly"
  )
);
const obsPreview = lazy(() =>
  import("./components/Views/ObsManagement/ObsPreview/ObsPreview")
);

const sectorListView = lazy(() =>
  import("./components/Views/SectorsManagement/SectorListView")
);
const SectorManagementView = lazy(() =>
  import("./components/Views/SectorsManagement/SectorManagementView")
);
const tourneeListView = lazy(() =>
  import("./components/Views/TourneeManagement/TourneeListView")
);
const TourneeManagementView = lazy(() =>
  import("./components/Views/TourneeManagement/TourneeManagementView")
);
const DailyReportsView = lazy(() =>
  import("./components/Views/DailyReports/DailyReportsView")
);
const TaskManagementView = lazy(() =>
  import("./components/Views/TaskManagement/TaskManagementView")
);
const EngagementView = lazy(() =>
  import("./components/Views/Gamification/EngagementView")
);
const MyNotificationsView = lazy(() =>
  import("./components/Views/Notifications/MyNotificationsView")
);
const MyActivitiesView = lazy(() =>
  import("./components/Views/Activities/MyActivitiesView")
);
const DebugView = lazy(() =>
  import("./components/Views/Debug/DebugView")
);
const LiveSectorView = lazy(() =>
  import("./components/Views/Live/LiveSectorView")
);

const quizzManagementView = lazy(() =>
  import("./components/Views/Quizz/QuizzManagementView/QuizzManagementView")
);
const quizzManagementView2 = lazy(() =>
  import("./components/Views/Quizz/QuizzManagementView/QuizzManagementView2")
);
const ProfilComparisonView = lazy(() =>
  import("./components/Views/Quizz/QuizzManagementView/ProfilComparisonView")
);

const quizzListView = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/QuizzListView")
);
const quizzListView2 = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/QuizzListView2")
);
const quizzListView3 = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/QuizzListView3")
);
const BiblioView = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/BiblioView")
);

const QuizzProfileView = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/QuizzProfilView")
);
const QuizzListViewReadOnly = lazy(() =>
  import("./components/Views/Quizz/QuizzListView/QuizzListReadOnlyView")
);
const QuizzResultView = lazy(() =>
  import("./components/Views/Quizz/QuizzResultView/QuizzResultView")
);
const QuizzResultDemo = lazy(() =>
  import("./components/Views/Quizz/QuizzResultView/QuizzResult_demo")
);
// EvalHistory utilise Firestore par défaut
const EvalHistory = lazy(() =>
  import("./components/Views/Quizz/EvalHistory/EvalHistoryFirestore")
);
// EvalHistoryRealtime pour debug (akongoStaff only)
const EvalHistoryRealtime = lazy(() =>
  import("./components/Views/Quizz/EvalHistory/EvalHistory")
);

const QuizzPreview = lazy(() =>
  import("./components/Views/Quizz/QuizzResultView/QuizzPreview")
);
const QuizzPreview2 = lazy(() =>
  import("./components/Views/Quizz/QuizzResultView/QuizzPreview2")
);
const QuizzPreviewInternal = lazy(() =>
  import("./components/Views/Quizz/QuizzResultView/QuizzPreviewInternal")
);

const QuestionComparisonView = lazy(() =>
  import(
    "./components/Views/Quizz/QuestionComparisonView/QuestionComparisonView"
  )
);
const QuestionTimeMachine = lazy(() =>
  import("./components/Views/Quizz/QuestionTimeMachine/QuestionTimeMachine")
);

const RandomView = lazy(() =>
  import("./components/Views/FakeDataGenerator/RandomView")
);

const Dashboard = lazy(() => import("./components/Views/Dashboard/Dashboard"));
const DashboardNext = lazy(() => import("./components/Views/Dashboard/DashboardNext"));
const RafikiFullView = lazy(() => import("./components/Views/RafikiFullView/RafikiFullView"));
const DashboardCustom = lazy(() => import("./components/Views/Dashboard/DashboardCustomView"));
const DashboardSpecie = lazy(() =>
  import("./components/Views/SpecieManagement/DashboardSpecie")
);

const SpecieRecap = lazy(() =>
  import("./components/Views/SpecieManagement/SpecieRecap")
);
const Timeline = lazy(() => import("./components/Views/Timeline/Timeline"));

const GalleryView = lazy(() => import("./components/Views/Media/GalleryView"));

const TagManagementView = lazy(() =>
  import("./components/Views/TagManagement/TagManagementView")
);

const DocumentTagsManagementView = lazy(() =>
  import("./components/Views/DocumentTagsManagement/DocumentTagsManagementView")
);

const ZooList = lazy(() => import("./components/Views/ZooList/ZooList"));
const ZooDetails = lazy(() => import("./components/Views/ZooList/ZooDetails"));
const AdminSettingsView = lazy(() => import("./components/Views/AdminSettings/AdminSettingsView"));
const OfferMigrationView = lazy(() => import("./components/Views/OfferMigration/OfferMigrationView"));
const DiscoveryAnalyticsView = lazy(() => import("./components/Views/DiscoveryAnalytics/DiscoveryAnalyticsView"));
const PaidAccountsAnalyticsView = lazy(() => import("./components/Views/PaidAccountsAnalytics/PaidAccountsAnalyticsView"));
const PublicApiAnalyticsView = lazy(() => import("./components/Views/PublicApiAnalytics/PublicApiAnalyticsView"));
const ZimsSyncDashboardView = lazy(() => import("./components/Views/ZimsSyncDashboard/ZimsSyncDashboardView"));
const ZimsCollectionView = lazy(() => import("./components/Views/ZimsCollection/ZimsCollectionView"));
const WeatherView = lazy(() => import("./components/Views/Weather/WeatherView"));

// Fast Evaluation Views
const GroupResultsView = lazy(() => import("./components/Views/FastEval/GroupResultsView"));
const FastEvalGroupListView = lazy(() => import("./components/Views/FastEval/GroupListView"));
const IndividualFastEvalListView = lazy(() => import("./components/Views/FastEval/IndividualFastEvalListView"));
const FastGroupManagementListView = lazy(() => import("./components/Views/FastEval/FastGroupManagementListView"));
const FastEvalGroupComparisonView = lazy(() => import("./components/Views/FastEval/GroupComparisonView"));
const FastEvaluationView = lazy(() => import("./components/Views/FastEval/SimpleFastEvaluationView"));
const GroupGalleryView = lazy(() => import("./components/Views/FastEval/GroupGalleryView"));
const GroupActionsView = lazy(() => import("./components/Views/FastEval/GroupActionsView"));
const ObjectivesListView = lazy(() => import("./components/Views/FastEval/ObjectivesListView"));
const AdvancedGroupManagementView = lazy(() => import("./components/Views/FastEval/AdvancedGroupManagementView"));
const GridManagementView = lazy(() => import("./components/Views/FastEval/GridManagementView"));
const GridBuilderView = lazy(() => import("./components/Views/FastEval/GridBuilderView"));
const GridImportView = lazy(() => import("./components/Views/FastEval/GridImportView"));
const GridDetailView = lazy(() => import("./components/Views/FastEval/GridDetailView"));

const Login = lazy(() => import("./components/Views/Login/Login"));
const Activation = lazy(() => import("./components/Views/Login/activation/Activation"));
const Register = lazy(() => import("./components/Views/Login/Register"));
const ScanResultView = lazy(() =>
  import("./components/Views/ScanManagement/ScanResultView")
);
const ScanAnalysisListView = lazy(() =>
  import("./components/Views/ScanManagement/ScanAnalysisListView")
);
const FocalAnalysisView = lazy(() =>
  import("./components/Views/FocalManagement/FocalAnalysisView")
);
const FocalSamplingListView = lazy(() =>
  import("./components/Views/FocalManagement/FocalSamplingListView")
);
const QuizzAnalysisListView = lazy(() =>
  import("./components/Views/QuizzAnalysis/QuizzAnalysisListView")
);
const MultiParameterAnalysisView = lazy(() =>
  import("./components/Views/MultiParameterAnalysis/MultiParameterAnalysisView")
);
const WeightAnalysisView = lazy(() =>
  import("./components/Views/WeightAnalysis/WeightAnalysisView")
);
const QuizzAnalysisView = lazy(() =>
  import("./components/Views/QuizzAnalysis/QuizzAnalysisView")
);
const VeterinaryParametersView = lazy(() =>
  import("./components/Views/HealthManagement/VeterinaryParametersView")
);
const VeterinaryDashboardView = lazy(() =>
  import("./components/Views/HealthManagement/VeterinaryDashboardView")
);
const TechnicalDashboardView = lazy(() =>
  import("./components/Views/TechnicalManagement/TechnicalDashboardView")
);
const CustomParametersHubView = lazy(() =>
  import("./components/Views/CustomParametersHub/CustomParametersHubView")
);
const TrainingExercisesView = lazy(() =>
  import("./components/Views/Training/TrainingExercisesView")
);
const TrainingDashboardView = lazy(() =>
  import("./components/Views/Training/TrainingDashboardView")
);
const AppStoreRedirect = lazy(() =>
  import("./components/Views/AppStoreRedirect/AppStoreRedirect")
);

// Help Center
const HelpCenterView = lazy(() =>
  import("./components/Views/HelpCenter/HelpCenterView")
);
const SupportTicketsView = lazy(() =>
  import("./components/Views/HelpCenter/SupportTicketsView")
);
const AdminTicketsView = lazy(() =>
  import("./components/Views/HelpCenter/AdminTicketsView")
);

// Pages de design (catalogue, audit, prototypes) : serveur local et tests
// seulement. Elles montrent des écrans internes, dont ceux du CRM : les builds
// de production et de staging (NODE_ENV=production) n'ont ni ces routes ni leur
// code. La condition est écrite à chaque import pour que webpack écarte les
// chunks à la compilation (une constante intermédiaire ne suffit pas).
const DESIGN_PAGES_AVAILABLE = process.env.NODE_ENV !== "production";
const DesignSystemPrototypePage = process.env.NODE_ENV !== "production"
  ? waitFor(lazy(() => import("./components/Views/DesignSystemPrototype/DesignSystemPrototype")))
  : null;
const DesignReferencePage = process.env.NODE_ENV !== "production"
  ? waitFor(lazy(() => import("./components/Views/DesignReferencePrototype/DesignReferencePrototype")))
  : null;
const DesignSystemAuditPage = process.env.NODE_ENV !== "production"
  ? waitFor(lazy(() => import("./components/Views/DesignSystemAudit/DesignSystemAudit")))
  : null;
const DesignSystemV2CataloguePage = process.env.NODE_ENV !== "production"
  ? waitFor(lazy(() => import("./components/Views/DesignSystemV2Catalogue/DesignSystemV2Catalogue")))
  : null;
const WelfareNavigationEntry = lazy(() =>
  import("./navigation-v2/app/WelfareNavigationEntry")
);
const IndividualsRegistryEntry = lazy(welfareEntityRouteLoaders["collection.individuals"]);
const IndividualDetailRoute = lazy(welfareEntityRouteLoaders["collection.individual"]);
const VeterinaryDashboardEntry = lazy(() =>
  import("./veterinary-v2/app/VeterinaryDashboardEntry")
);
const SectorWorkspaceRoute = lazy(welfareEntityRouteLoaders["collection.sector"]);
const SectorListRoute = lazy(welfareEntityRouteLoaders["collection.sectors"]);
const ZooDetailsRoute = lazy(() =>
  import("./zoo-details-v2/app/ZooDetailsRoute")
);
const EnclosuresRoute = lazy(welfareEntityRouteLoaders["collection.enclosures"]);
const TimelineEntry = lazy(() =>
  import("./timeline-v2/app/TimelineEntry")
);
const LiveSectorRoute = lazy(() =>
  import("./live-sector-v2/app/LiveSectorRoute")
);
const DashboardRoute = lazy(() =>
  import("./dashboard-v2/app/DashboardRoute")
);
const GettingStartedRoute = lazy(() =>
  import("./onboarding-v2/app/GettingStartedRoute")
);
const EstablishmentCreationRoute = lazy(() =>
  import("./establishment-creation-v2/app/EstablishmentCreationRoute")
);
const OnboardingAnalyticsRoute = lazy(() =>
  import("./onboarding-analytics-v2/app/OnboardingAnalyticsRoute")
);
const PharmacyCommunityReviewRoute = lazy(() =>
  import("./pharmacy-community-v2/app/PharmacyCommunityReviewRoute")
);
// Base commune des aliments (staff AKONGO, GitLab #1104).
const FoodCatalogReviewRoute = lazy(() =>
  import("./food-catalog-v2/app/FoodCatalogReviewRoute")
);
const CrmRoute = lazy(() => import("./crm-v2/app/CrmRoute"));
const BehaviorRegistryRoute = lazy(() => import("./ethograms-v2/app/BehaviorRegistryRoute"));
const ParkEthogramRoute = lazy(() => import("./ethograms-v2/app/ParkEthogramRoute"));
const GroupsRoute = lazy(welfareEntityRouteLoaders["collection.groups"]);
const AccountRoute = lazy(() =>
  import("./account-v2/app/AccountRoute")
);
const TasksRoute = lazy(() =>
  import("./tasks-v2/app/TasksRoute")
);
const ToursRoute = lazy(welfareEntityRouteLoaders["operations.tours"]);
const DailyReportsRoute = lazy(() =>
  import("./daily-reports-v2/app/DailyReportsRoute")
);
const WelfareFollowUpPreview = process.env.NODE_ENV === "development" ? lazy(() => import("./welfare-follow-up/PreviewRoute")) : null;
const ObjectivesRoute = lazy(() =>
  import("./objectives-v2/app/ObjectivesRoute")
);
const AdvancedReportsRoute = lazy(() =>
  import("./advanced-reports-v2/app/AdvancedReportsRoute")
);
const EnrichmentsRoute = lazy(() =>
  import("./enrichments-v2/app/EnrichmentsRoute")
);
const EnrichmentQuickInstallRoute = lazy(() =>
  import("./enrichment-quick-install-v2/app/QuickInstallRoute")
);
const EnrichmentTagsRoute = lazy(() =>
  import("./enrichment-tags-v2/app/EnrichmentTagsRoute")
);
const CustomParametersRoute = lazy(() =>
  import("./custom-parameters-v2/app/CustomParametersRoute")
);
const AnomalyAlertsRoute = lazy(() =>
  import("./anomaly-alerts-v2/app/AnomalyAlertsRoute")
);
const ResearchProjectsRoute = lazy(() =>
  import("./research-projects-v2/app/ResearchProjectsRoute")
);
const CampaignsRoute = lazy(() =>
  import("./campaigns-v2/app/CampaignsRoute")
);
const FocalWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/focal/FocalWelfareRoute")
);
const ScanWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/scan/ScanWelfareRoute")
);
const WelfareMonitoringRoute = lazy(() =>
  import("./welfare-monitoring-v2/app/WelfareMonitoringRoute")
);
const QuestionnaireWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/questionnaire/QuestionnaireWelfareRoute")
);
const EnrichmentWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/enrichment/EnrichmentWelfareRoute")
);
const MultiParameterWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/multi-parameter/MultiParameterWelfareRoute")
);
const WeightWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/weight/WeightWelfareRoute")
);
const TrainingWelfareRoute = lazy(() =>
  import("./scientific-analysis-v2/training/TrainingWelfareRoute")
);
const FastEvaluationsRoute = lazy(() =>
  import("./fast-evaluations-v2/app/FastEvaluationsRoute")
);
const FastEvaluationResultsRoute = lazy(() =>
  import("./fast-evaluation-results-v2/app/FastEvaluationResultsRoute")
);
const ResourcesRoute = lazy(() =>
  import("./resources-v2/app/ResourcesRoute")
);
const QuestionnaireResourceRoute = lazy(() =>
  import("./resources-v2/app/QuestionnaireResourceRoute")
);
const BlankGridRoute = lazy(() =>
  import("./resources-v2/blank-grid/BlankGridRoute")
);
const ResourceEditorsWelfareRoute = lazy(() =>
  import("./resource-editors-v2/app/ResourceEditorsWelfareRoute")
);
const TechnicalRoute = lazy(() =>
  import("./technical-v2/app/TechnicalRoute")
);
const ActivitiesWelfareRoute = lazy(() =>
  import("./activities-v2/app/ActivitiesWelfareRoute")
);
const UsersRoute = lazy(() => import("./users-v2/app/UsersRoute"));
const SpeciesRoute = lazy(() => import("./species-v2/app/SpeciesRoute"));
const AssistanceRoute = lazy(() =>
  import("./assistance-v2/app/AssistanceRoute")
);
const ParkIntegrationsRoute = lazy(() =>
  import("./park-integrations-v2/app/ParkIntegrationsRoute")
);
const EngagementRoute = lazy(() =>
  import("./engagement-v2/app/EngagementRoute")
);
const MediaSocialRoute = lazy(() =>
  import("./media-social-v2/app/MediaSocialRoute")
);
const ObserverTrainingSessionsRoute = lazy(() =>
  import("./observer-training-sessions-v2/app/ObserverTrainingSessionsRoute")
);
const CollectionResultsRoute = lazy(() =>
  import("./collection-results-v2/app/CollectionResultsRoute")
);
const EnrichmentGuideRoute = lazy(() =>
  import("./enrichment-guides-v2/app/EnrichmentGuideRoute")
);
const AkongoAdminRoute = lazy(() =>
  import("./akongo-admin-v2/app/AkongoAdminRoute")
);

// Document Management
const DocumentListView = lazy(() =>
  import("./components/Views/DocumentManagement/DocumentListView")
);

// Debug Views
const RafikiTranslationDebugView = lazy(() =>
  import("./components/Views/Debug/RafikiTranslationDebugView")
);
const EthogramTranslationDebugView = lazy(() =>
  import("./components/Views/Debug/EthogramTranslationDebugView")
);
const ContextTranslationDebugView = lazy(() =>
  import("./components/Views/Debug/ContextTranslationDebugView")
);
const TranslationProposalsView = lazy(() =>
  import("./components/Views/TranslationProposals/TranslationProposalsView")
);
const TranslationCacheAdminView = lazy(() =>
  import("./components/Views/TranslationCacheAdmin/TranslationCacheAdminView")
);
const TimeTrackingView = lazy(() =>
  import("./components/Views/TimeTracking/TimeTrackingView")
);
const AkongoProjectsView = lazy(() =>
  import("./components/Views/TimeTracking/AkongoProjectsView")
);
const AkongoProjectDetailView = lazy(() =>
  import("./components/Views/TimeTracking/AkongoProjectDetailView")
);
const BibliographyView = lazy(() =>
  import("./components/Views/Bibliography/BibliographyView")
);
const DeeplStatsView = lazy(() =>
  import("./components/Views/Debug/DeeplStatsView")
);
const RafikiStatsView = lazy(() =>
  import("./components/Views/Debug/RafikiStatsView")
);
const DeferredObsDebug = lazy(() =>
  import("./components/Views/Debug/DeferredObsDebug")
);
const MissingEffectiveTimestampView = lazy(() =>
  import("./components/Views/Debug/MissingEffectiveTimestampView")
);
// List of routes that uses the page layout
// listed here to Switch between layouts
// depending on the current pathname
const listofPages = [
  "/login",
  "/activate",
  "/Register",
  "/export",
  "/rafiki-artifact",
  "/app-store",
  "/print/eaza-report",
  // Absentes des builds de production : une de ces adresses y suit le chemin
  // d'une page inconnue (connexion ou accueil) au lieu d'un écran vide.
  ...(DESIGN_PAGES_AVAILABLE
    ? ["/design-system-prototype", "/design-reference", "/design-system-audit", "/design-system-v2"]
    : []),
  "/preview/welfare-follow-up",
  "/v2",
  /* See full project for reference */
];

const RafikiArtifactPage = lazy(() => import("./components/Common/RafikiArtifacts/RafikiArtifactPage"));

const EazaReportPrint = lazy(() =>
  import("./components/Views/Enrichissements/EazaReportPrint/EazaReportPrint")
);

const Routes = ({ location }) => {
  const currentKey = location.pathname.split("/")[1] || "/";
  const timeout = { enter: 500, exit: 500 };

  // Animations supported
  //      'rag-fadeIn'
  //      'rag-fadeInRight'
  //      'rag-fadeInLeft'

  const animationName = "rag-fadeIn";

  if (
    listofPages.indexOf(location.pathname) > -1 ||
    location.pathname.startsWith("/v2/")
  ) {
    const welfareV2 = location.pathname === "/v2" || location.pathname.startsWith("/v2/");
    const pageSwitch = (
          <Switch location={location}>
            {WelfareFollowUpPreview && <Route exact path="/preview/welfare-follow-up" component={waitFor(WelfareFollowUpPreview)} />}
            <Route path="/activate" component={waitFor(Activation)} />
            <Route path="/Register" component={waitFor(Register)} />
            <Route path="/login" component={waitFor(Login)} />
            <Route path="/export" render={(props) => <StandaloneProductBoundary><QuizzPreview2 {...props} /></StandaloneProductBoundary>} />
            <Route path="/rafiki-artifact" render={(props) => <StandaloneProductBoundary><RafikiArtifactPage {...props} /></StandaloneProductBoundary>} />
            <Route path="/app-store" component={waitFor(AppStoreRedirect)} />
            {/* Machine renderer authenticates its scoped custom token before any report reads. */}
            <Route path="/print/eaza-report" component={waitFor(EazaReportPrint)} />
            {/* Pages de design : serveur local et tests seulement (voir leurs imports). */}
            {DESIGN_PAGES_AVAILABLE && <Route path="/design-system-prototype" component={DesignSystemPrototypePage} />}
            {DESIGN_PAGES_AVAILABLE && <Route path="/design-reference" component={DesignReferencePage} />}
            {DESIGN_PAGES_AVAILABLE && <Route path="/design-system-audit" component={DesignSystemAuditPage} />}
            {DESIGN_PAGES_AVAILABLE && <Route path="/design-system-v2" component={DesignSystemV2CataloguePage} />}
            <Route
              exact
              path="/v2/individuals"
              component={waitFor(IndividualsRegistryEntry)}
            />
            <Route
              exact
              path="/v2/individuals/import/zims"
              render={({ location }) => <Redirect to={{ pathname: "/v2/integrations/zims", search: "?tab=import&overlay=file", state: location.state }} />}
            />
            <Route
              exact
              path="/v2/individuals/new"
              component={waitFor(IndividualDetailRoute)}
            />
            <Route
              path="/v2/individuals/:animalId"
              component={waitFor(IndividualDetailRoute)}
            />
            <Route
              path="/v2/veterinary/:group?"
              component={waitFor(VeterinaryDashboardEntry)}
            />
            <Route
              exact
              path="/v2/sectors"
              component={waitFor(SectorListRoute)}
            />
            <Route
              path="/v2/sectors/:sectorId"
              component={waitFor(SectorWorkspaceRoute)}
            />
            <Route
              path="/v2/establishment"
              component={waitFor(ZooDetailsRoute)}
            />
            <Route
              path="/v2/enclosures/:enclosureId?"
              component={waitFor(EnclosuresRoute)}
            />
            <Route
              path="/v2/timeline"
              component={waitFor(TimelineEntry)}
            />
            <Route
              path="/v2/live-sectors"
              component={waitFor(LiveSectorRoute)}
            />
            <Route
              path="/v2/dashboard"
              component={waitFor(DashboardRoute)}
            />
            <Route
              path="/v2/getting-started"
              component={waitFor(GettingStartedRoute)}
            />
            <Route
              exact
              path="/v2/groups/:groupId?"
              component={waitFor(GroupsRoute)}
            />
            <Route
              path="/v2/account"
              component={waitFor(AccountRoute)}
            />
            <Route
              path="/v2/tasks"
              component={waitFor(TasksRoute)}
            />
            <Route
              exact
              path="/v2/tours/editor/:tourneeId?"
              component={waitFor(ToursRoute)}
            />
            <Route
              exact
              path="/v2/tours"
              component={waitFor(ToursRoute)}
            />
            <Route
              path="/v2/daily-reports"
              component={waitFor(DailyReportsRoute)}
            />
            <Route
              path="/v2/objectives/group/:groupId"
              component={waitFor(ObjectivesRoute)}
            />
            <Route
              exact
              path="/v2/objectives"
              component={waitFor(ObjectivesRoute)}
            />
            <Route
              path="/v2/advanced-reports/:visitId?"
              component={waitFor(AdvancedReportsRoute)}
            />
            <Route
              exact
              path="/v2/enrichments/quick-install"
              component={waitFor(EnrichmentQuickInstallRoute)}
            />
            <Route
              exact
              path="/v2/enrichments/tags"
              component={waitFor(EnrichmentTagsRoute)}
            />
            <Route
              exact
              path="/v2/enrichments/calendar"
              component={waitFor(EnrichmentsRoute)}
            />
            <Route
              exact
              path="/v2/enrichments/editor/:enrichId?"
              component={waitFor(EnrichmentsRoute)}
            />
            <Route
              exact
              path="/v2/enrichments/new"
              render={({ location }) => <Redirect to={{ pathname: "/v2/enrichments/editor", search: location.search, state: location.state }} />}
            />
            <Route
              exact
              path="/v2/enrichments/:enrichmentId"
              render={({ location, match }) => <Redirect to={{ pathname: `/v2/enrichments/editor/${encodeURIComponent(match.params.enrichmentId)}`, search: location.search, state: location.state }} />}
            />
            <Route
              exact
              path="/v2/enrichments"
              component={waitFor(EnrichmentsRoute)}
            />
            <Route
              path="/v2/custom-parameters"
              component={waitFor(CustomParametersRoute)}
            />
            <Route
              exact
              path="/v2/alerts"
              component={waitFor(AnomalyAlertsRoute)}
            />
            <Route
              path="/v2/research-projects/:view?/:projectId?"
              component={waitFor(ResearchProjectsRoute)}
            />
            <Route
              path="/v2/campaigns/:campaignId?"
              component={waitFor(CampaignsRoute)}
            />
            <Route
              path="/v2/analysis/focal/:mode?"
              component={waitFor(FocalWelfareRoute)}
            />
            <Route
              path="/v2/analysis/scan/:mode?"
              component={waitFor(ScanWelfareRoute)}
            />
            <Route
              exact
              path="/v2/welfare/results/questionnaire/demo"
              component={waitFor(CollectionResultsRoute)}
            />
            <Route
              exact
              path="/v2/welfare/results/questionnaire"
              component={waitFor(CollectionResultsRoute)}
            />
            <Route
              exact
              path="/v2/welfare/results/observation"
              component={waitFor(CollectionResultsRoute)}
            />
            <Route
              path="/v2/welfare/:mode"
              component={waitFor(WelfareMonitoringRoute)}
            />
            <Route
              path="/v2/analysis/questionnaire/:mode?"
              component={waitFor(QuestionnaireWelfareRoute)}
            />
            <Route
              path="/v2/analysis/enrichment/:mode?"
              component={waitFor(EnrichmentWelfareRoute)}
            />
            <Route
              path="/v2/analysis/multi-parameter"
              component={waitFor(MultiParameterWelfareRoute)}
            />
            <Route
              path="/v2/analysis/weight"
              component={waitFor(WeightWelfareRoute)}
            />
            <Route
              path="/v2/analysis/training"
              component={waitFor(TrainingWelfareRoute)}
            />
            <Route
              path="/v2/fast-evaluations/results"
              component={waitFor(FastEvaluationResultsRoute)}
            />
            <Route
              path="/v2/fast-evaluations/gallery"
              component={waitFor(FastEvaluationResultsRoute)}
            />
            <Route
              path="/v2/fast-evaluations/comparison"
              component={waitFor(FastEvaluationResultsRoute)}
            />
            <Route
              path="/v2/fast-evaluations/:mode?"
              component={waitFor(FastEvaluationsRoute)}
            />
            <Route
              exact
              path="/v2/resources/grids/import"
              component={waitFor(ResourceEditorsWelfareRoute)}
            />
            <Route
              exact
              path="/v2/resources/grids/new"
              component={waitFor(ResourceEditorsWelfareRoute)}
            />
            <Route
              exact
              path="/v2/resources/grids/:gridId/edit"
              component={waitFor(ResourceEditorsWelfareRoute)}
            />
            <Route
              exact
              path="/v2/resources/grids/:gridId/detail"
              component={waitFor(ResourceEditorsWelfareRoute)}
            />
            <Route
              exact
              path="/v2/resources/ethograms/adapt"
              component={waitFor(ParkEthogramRoute)}
            />
            <Route
              path="/v2/resources/ethograms/v2"
              component={waitFor(ParkEthogramRoute)}
            />
            <Route
              exact
              path="/v2/resources/ethograms/:obsId/preview"
              component={waitFor(ResourceEditorsWelfareRoute)}
            />
            <Route
              exact
              path="/v2/resources/observerTraining/session"
              component={waitFor(ObserverTrainingSessionsRoute)}
            />
            <Route
              exact
              path="/v2/resources/observerTraining/video"
              component={waitFor(ObserverTrainingSessionsRoute)}
            />
            <Route
              exact
              path="/v2/resources/questionnaires/:surveyId/:kind"
              component={waitFor(QuestionnaireResourceRoute)}
            />
            <Route
              exact
              path="/v2/resources/blank-grid"
              component={waitFor(BlankGridRoute)}
            />
            <Route
              path="/v2/resources/:domain?"
              component={waitFor(ResourcesRoute)}
            />
            <Route
              path="/v2/technical/live/:enclosureId"
              component={waitFor(TechnicalRoute)}
            />
            <Route
              path="/v2/technical"
              component={waitFor(TechnicalRoute)}
            />
            <Route
              path="/v2/activities/:section?"
              component={waitFor(ActivitiesWelfareRoute)}
            />
            <Route
              path="/v2/users/:mode?"
              component={waitFor(UsersRoute)}
            />
            <Route
              path="/v2/species/:specieId?"
              component={waitFor(SpeciesRoute)}
            />
            <Route
              path="/v2/assistance/:workspace?"
              component={waitFor(AssistanceRoute)}
            />
            <Route
              path="/v2/integrations/:module"
              component={waitFor(ParkIntegrationsRoute)}
            />
            <Route
              path="/v2/engagement"
              component={waitFor(EngagementRoute)}
            />
            <Route
              path="/v2/gallery/:entityType?/:entityId?"
              component={waitFor(MediaSocialRoute)}
            />
            <Route
              path="/v2/social/:entityType?/:entityId?"
              component={waitFor(MediaSocialRoute)}
            />
            <Route
              exact
              path="/v2/enrichment-guides/editor/:guideId?"
              component={waitFor(EnrichmentGuideRoute)}
            />
            <Route
              exact
              path="/v2/enrichment-guides/:guideId"
              component={waitFor(EnrichmentGuideRoute)}
            />
            <Route
              exact
              path="/v2/akongo/administration/establishments/new"
              component={waitFor(EstablishmentCreationRoute)}
            />
            {/* CRM de l'équipe : fiches et onglets servis par une seule route (pas de remontage entre vues). */}
            <Route path="/v2/akongo/crm" component={waitFor(CrmRoute)} />
            {/* Éthogrammes V2 (staff) : registre, éthogramme, fiche — une seule route, avant le repli générique `/v2/akongo`. */}
            <Route path="/v2/akongo/observations/behavior-registry" component={waitFor(BehaviorRegistryRoute)} />
            <Route
              exact
              path="/v2/akongo/analytics/getting-started"
              component={waitFor(OnboardingAnalyticsRoute)}
            />
            <Route
              exact
              path="/v2/akongo/administration/pharmacy-community"
              component={waitFor(PharmacyCommunityReviewRoute)}
            />
            <Route
              exact
              path="/v2/akongo/administration/food-catalog"
              component={waitFor(FoodCatalogReviewRoute)}
            />
            <Route
              path="/v2/akongo"
              component={waitFor(AkongoAdminRoute)}
            />
            <Route path="/v2" component={waitFor(WelfareNavigationEntry)} />
          </Switch>
    );
    if (welfareV2) {
      // Le shell V2 (chunk a la demande) reste monte autour des routes /v2 ;
      // le Suspense externe ne couvre que le chargement de ce chunk.
      return (
        <BasePage>
          {/* Premier écran V2 dès le bundle principal : jamais le chargeur
              Classique pendant le téléchargement du chunk du shell. */}
          <Suspense fallback={<WelfareBootScreen />}>
            <PersistentWelfareShellHost>
              {/* Revenir, c'est retrouver : chaque adresse /v2 affichée et sa
                  position de défilement sont retenues pour l'onglet. */}
              <ReturnMemoryRecorder />
              {/* Bascule du mode démo / langue en démo : remonte les routes
                  /v2 (pas le shell) pour relire les données anonymisées. */}
              <DemoModeRemountBoundary>
                <Suspense fallback={<WelfareRouteLoader />}>
                  <WelfareProductBoundary>{pageSwitch}</WelfareProductBoundary>
                </Suspense>
              </DemoModeRemountBoundary>
            </PersistentWelfareShellHost>
          </Suspense>
        </BasePage>
      );
    }
    // Pages sans layout hors /v2 (login, export…) : ni shell ni chunk V2.
    return (
      <BasePage>
        <Suspense fallback={<PageLoader />}>{pageSwitch}</Suspense>
      </BasePage>
    );
  } else {
    return (
      <LegacyAuthenticationGate>
      <LegacyEntryGate>
      <Base>
        <TransitionGroup>
          <CSSTransition
            key={currentKey}
            timeout={timeout}
            classNames={animationName}
            exit={false}
          >
            <div>
              <Suspense fallback={<PageLoader />}>
                <LegacyRouteGuard location={location}>
                <Switch location={location}>
                  <Route path="/singleview" component={waitFor(SingleView)} />
                  <Route path="/submenu" component={waitFor(SubMenu)} />
                  <Route path="/testView" component={waitFor(testView)} />
                  <Route
                    path="/specieAnimalsListView"
                    component={waitForEntity(specieAnimalsListView, ["taxonomyType", "taxonomyId", "specieId"])}
                  />
                  <Route
                    path="/AssesmentView"
                    component={waitFor(AssesmentView)}
                  />
                  <Route
                    path="/AnimalManagementView"
                    component={waitForEntity(animalManagementView, ["animalId"])}
                  />
                  <Route
                    path="/AdvancedZimsImportView"
                    render={() => <Redirect to={{ pathname: "/ZimsCollection", search: "?import=file" }} />}
                  />
                  <Route
                    path="/VeterinaryParametersView"
                    component={waitFor(VeterinaryParametersView)}
                  />
                  <Route
                    path="/VeterinaryDashboardView/:group?"
                    component={waitFor(VeterinaryDashboardView)}
                  />
                  {/* La route n'est pas gardée par rôle : la vue vérifie elle-même
                      canAccessTechnicalModule au montage (accès par URL directe). */}
                  <Route
                    path="/TechnicalDashboardView/:group?"
                    component={waitFor(TechnicalDashboardView)}
                  />
                  <Route
                    path="/CustomParametersHubView"
                    component={waitFor(CustomParametersHubView)}
                  />
                  <Route
                    path="/TrainingExercisesView"
                    component={waitFor(TrainingExercisesView)}
                  />
                  <Route
                    path="/TrainingDashboardView"
                    component={waitFor(TrainingDashboardView)}
                  />
                  <Route
                    path="/AnimalStatView"
                    component={waitFor(AnimalStatView)}
                  />
                  <Route
                    path="/GroupListView"
                    component={waitFor(GroupListView)}
                  />
                  <Route path="/SocialView" component={waitFor(SocialView)} />

                  <Route
                    path="/ReportListView"
                    component={waitFor(ReportListView)}
                  />
                  <Route
                    path="/ReportManagementView"
                    component={waitFor(ReportManagementView)}
                  />

                  <Route
                    path="/SpecieListManagement"
                    component={waitFor(SpecieListManagement)}
                  />
                  <Route
                    path="/SpecieManagementView"
                    component={waitFor(SpecieManagementView)}
                  />
                  <Route path="/SpecieQA" component={waitFor(SpecieQA)} />
                  <Route path="/SpecieRecap" component={waitFor(SpecieRecap)} />
                  <Route
                    path="/IUCNMigrationTool"
                    component={waitFor(IUCNMigrationTool)}
                  />
                  <Route
                    path="/IUCNSpeciesSearch" 
                    render={(props) => <IUCNSpeciesSearch {...props} standalone={true} />}
                  />
                  <Route 
                    path="/IUCNSpecieForm" 
                    component={waitFor(IUCNSpecieForm)} 
                  />
                  <Route path="/Timeline" component={waitFor(Timeline)} />
                  <Route
                    path="/FamilyListManagement"
                    component={waitFor(FamilyListManagement)}
                  />
                  <Route
                    path="/OrderListManagement"
                    component={waitFor(OrderListManagement)}
                  />
                  <Route
                    path="/GroupManagementView"
                    component={waitFor(GroupManagementView)}
                  />
                  <Route
                    path="/EnclosureListView"
                    component={waitFor(EnclosureListView)}
                  />
                  <Route
                    path="/EnclosureManagementView"
                    component={waitForEntity(EnclosureManagementView, ["enclosureId"])}
                  />
                  <Route
                    path="/EnclosureSensorView"
                    component={waitFor(EnclosureSensorView)}
                  />
                  <Route
                    path="/EnrichListView"
                    component={waitFor(EnrichListView)}
                  />
                  <Route
                    path="/EnrichManagementView"
                    component={waitForEntity(EnrichManagementView, ["enrichId"])}
                  />
                  <Route
                    path="/EnrichListHistory"
                    component={waitFor(EnrichListHistory)}
                  />
                  <Route
                    path="/EnrichmentAnalysisListView"
                    component={waitFor(EnrichmentAnalysisSelectorView)}
                  />
                  <Route
                    path="/EnrichmentAnalysisView"
                    component={waitFor(EnrichmentAnalysisListView)}
                  />

                  <Route
                    path="/EnrichCalendarView"
                    component={waitFor(EnrichCalendarView)}
                  />
                  <Route
                    path="/EnrichmentGuidesView"
                    component={waitFor(EnrichmentGuidesView)}
                  />
                  <Route
                    path="/EnrichmentGuideDetailView"
                    component={waitForEntity(EnrichmentGuideDetailView, ["guideId"])}
                  />
                  <Route
                    path="/EnrichmentGuideEditorView"
                    component={waitFor(EnrichmentGuideEditorView)}
                  />
                  
                  <Route
                    path="/TestSetupEnrich"
                    component={waitFor(TestSetupEnrichView)}
                  />

                  <Route
                    path="/CirManagementView"
                    component={waitFor(CirManagementView)}
                  />
                  <Route
                    path="/SectorListView"
                    component={waitFor(sectorListView)}
                  />
                  <Route
                    path="/SectorManagementView"
                    component={waitForEntity(SectorManagementView, ["sectorId"])}
                  />
                  <Route
                    path="/TourneeListView"
                    component={waitFor(tourneeListView)}
                  />
                  <Route
                    path="/TourneeManagementView"
                    component={waitForEntity(TourneeManagementView, ["tourneeId"])}
                  />
                  <Route
                    path="/DailyReportsView"
                    component={waitFor(DailyReportsView)}
                  />
                  <Route
                    path="/TaskManagementView"
                    component={waitFor(TaskManagementView)}
                  />
                  <Route
                    path="/EngagementView"
                    component={waitFor(EngagementView)}
                  />
                  <Route
                    path="/MyNotificationsView"
                    component={waitFor(MyNotificationsView)}
                  />
                  <Route
                    path="/MyActivitiesView"
                    component={waitFor(MyActivitiesView)}
                  />
                  <Route
                    path="/DebugView"
                    component={waitFor(DebugView)}
                  />
                  <Route
                    path="/TranslationProposalsView"
                    component={waitFor(TranslationProposalsView)}
                  />
                  <Route
                    path="/TranslationCacheAdminView"
                    component={waitFor(TranslationCacheAdminView)}
                  />
                  <Route
                    path="/TimeTrackingView"
                    component={waitFor(TimeTrackingView)}
                  />
                  <Route
                    path="/AkongoProjectsView"
                    component={waitFor(AkongoProjectsView)}
                  />
                  <Route
                    path="/AkongoProjectDetailView/:projectId"
                    component={waitFor(AkongoProjectDetailView)}
                  />
                  <Route
                    path="/BibliographyView"
                    component={waitFor(BibliographyView)}
                  />
                  <Route
                    path="/LiveSectorView"
                    component={waitFor(LiveSectorView)}
                  />
                  <Route
                    path="/CampaignCreationView"
                    component={waitFor(CampaignCreationView)}
                  />
                  <Route
                    path="/CampaignCreationViewOld"
                    component={waitFor(CampaignCreationViewOld)}
                  />

                  <Route
                    path="/campaignListView"
                    component={waitFor(campaignListView)}
                  />
                  <Route
                    path="/CampaignManagementView"
                    component={waitForEntity(CampaignManagementView, ["campaignId"])}
                  />

                  <Route
                    path="/CampaignQuizzMonitoringView"
                    component={waitFor(CampaignQuizzMonitoringView)}
                  />
                  <Route
                    path="/userCreation"
                    component={waitFor(userCreation)}
                  />
                  <Route
                    path="/userSettings"
                    component={waitForEntity(userSettings, ["userId"])}
                  />
                  <Route
                    path="/ResearchView"
                    component={waitFor(ResearchView)}
                  />
                  <Route
                    path="/ResearchListView"
                    component={waitFor(ResearchListView)}
                  />
                  <Route
                    path="/quizzManagementView"
                    component={waitFor(quizzManagementView)}
                  />
                  <Route
                    path="/quizzManagementView2"
                    component={waitFor(quizzManagementView2)}
                  />
                  <Route
                    path="/ProfilComparisonView"
                    component={waitFor(ProfilComparisonView)}
                  />
                  <Route
                    path="/quizzListView"
                    component={waitFor(quizzListView)}
                  />
                  <Route
                    path="/quizzListView2"
                    component={waitFor(quizzListView2)}
                  />
                  <Route
                    path="/quizzListView3"
                    component={waitFor(quizzListView3)}
                  />
                  <Route
                    path="/QuizzProfileView"
                    component={waitFor(QuizzProfileView)}
                  />
                  <Route path="/BiblioView" component={waitFor(BiblioView)} />
                  <Route
                    path="/QuizzListViewReadOnly"
                    component={waitFor(QuizzListViewReadOnly)}
                  />
                  <Route
                    path="/quizzResultView"
                    component={waitFor(QuizzResultView)}
                  />
                  <Route
                    path="/QuizzPreview"
                    component={waitFor(QuizzPreview)}
                  />
                  <Route
                    path="/QuizzPreview2"
                    component={waitForEntity(QuizzPreview2, ["surveyId"])}
                  />
                  <Route
                    path="/QuizzPreviewInternal"
                    component={waitFor(QuizzPreviewInternal)}
                  />
                  <Route path="/EvalHistory" component={waitFor(EvalHistory)} />
                  <Route path="/EvalHistoryRealtime" component={waitFor(EvalHistoryRealtime)} />
                  <Route
                    path="/QuizzResultDemo"
                    component={waitFor(QuizzResultDemo)}
                  />
                  <Route
                    path="/QuestionManagementView"
                    component={waitFor(QuestionManagementView)}
                  />
                  <Route
                    path="/QuestionComparisonView"
                    component={waitFor(QuestionComparisonView)}
                  />
                  <Route
                    path="/QuestionTimeMachine"
                    component={waitFor(QuestionTimeMachine)}
                  />
                  <Route path="/ObsListView" component={waitFor(ObsListView)} />
                  <Route
                    path="/ObsManagementView"
                    component={waitFor(ObsManagementView)}
                  />
                  <Route
                    path="/ObsResultView"
                    component={waitFor(ObsResultView)}
                  />
                  <Route path="/ObsPreview" component={waitForEntity(ObsPreview, ["obsId"])} />
                  <Route
                    path="/ObsSessionListView"
                    component={waitFor(ObsSessionListView)}
                  />
                  <Route
                    path="/ObsSessionView"
                    component={waitFor(ObsSessionView)}
                  />
                  <Route
                    path="/ObsComparisonView"
                    component={waitFor(ObsComparisonView)}
                  />
                  <Route
                    path="/ObsContextView"
                    component={waitFor(ObsContextView)}
                  />
                  <Route
                    path="/ObsContextListView"
                    component={waitFor(ObsContextListView)}
                  />
                  <Route
                    path="/obsListViewReadOnly"
                    component={waitFor(obsListViewReadOnly)}
                  />
                  <Route path="/obsPreview" component={waitFor(obsPreview)} />
                  <Route
                    path="/InterObsView"
                    component={waitFor(InterObsView)}
                  />
                  <Route
                    path="/ObsTrainingListView"
                    component={waitFor(ObsTrainingListView)}
                  />
                  <Route
                    path="/BlankGridView"
                    component={waitFor(BlankGridView)}
                  />
                  <Route
                    path="/ObsTrainingSessionView"
                    component={waitFor(ObsTrainingSessionView)}
                  />
                  <Route
                    path="/ObsTrainingVideoDetailView"
                    component={waitFor(ObsTrainingVideoDetailView)}
                  />
                  <Route path="/Dashboard" component={waitFor(Dashboard)} />
                  <Route path="/DashboardNext" component={waitFor(DashboardNext)} />
                  <Route path="/RafikiFullView" component={waitFor(RafikiFullView)} />
                  <Route path="/dashboard-custom" component={waitFor(DashboardCustom)} />
                  <Route
                    path="/DashboardSpecie"
                    component={waitFor(DashboardSpecie)}
                  />
                  <Route
                    path="/QuestionListView"
                    component={waitFor(QuestionListView)}
                  />
                  <Route
                    path="/QuestionShortManagement"
                    component={waitFor(QuestionShortManagement)}
                  />
                  <Route path="/GalleryView" component={waitFor(GalleryView)} />
                  <Route path="/ZooList" component={waitFor(ZooList)} />
                  <Route path="/ZooDetails" component={waitFor(ZooDetails)} />
                  <Route path="/AdminSettings" component={waitFor(AdminSettingsView)} />
                  <Route path="/OfferMigrationView" component={waitFor(OfferMigrationView)} />
                  <Route path="/DiscoveryAnalytics" component={waitFor(DiscoveryAnalyticsView)} />
                  <Route path="/PaidAccountsAnalytics" component={waitFor(PaidAccountsAnalyticsView)} />
                  <Route path="/PublicApiAnalytics" component={waitFor(PublicApiAnalyticsView)} />
                  <Route path="/ZimsSyncDashboard" component={waitFor(ZimsSyncDashboardView)} />
                  <Route path="/ZimsCollection" component={waitFor(ZimsCollectionView)} />
                  <Route path="/WeatherView" component={waitFor(WeatherView)} />
                  <Route path="/RandomView" component={waitFor(RandomView)} />
                  <Route
                    path="/TagManagementView"
                    component={waitFor(TagManagementView)}
                  />
                  <Route
                    path="/DocumentTagsManagementView"
                    component={waitFor(DocumentTagsManagementView)}
                  />
                  <Route
                    path="/ScanResultView"
                    component={waitFor(ScanResultView)}
                  />
                  <Route
                    path="/ScanAnalysisListView"
                    component={waitFor(ScanAnalysisListView)}
                  />
                  <Route
                    path="/FocalAnalysisView"
                    component={waitFor(FocalAnalysisView)}
                  />
                  <Route
                    path="/FocalSamplingListView"
                    component={waitFor(FocalSamplingListView)}
                  />
                  <Route
                    path="/QuizzAnalysisListView"
                    component={waitFor(QuizzAnalysisListView)}
                  />
                  <Route
                    path="/QuizzAnalysisView"
                    component={waitFor(QuizzAnalysisView)}
                  />
                  <Route
                    path="/MultiParameterAnalysisView"
                    component={waitFor(MultiParameterAnalysisView)}
                  />
                  <Route
                    path="/WeightAnalysisView"
                    component={waitFor(WeightAnalysisView)}
                  />
                  <Route
                    path="/GroupResultsView"
                    component={waitFor(GroupResultsView)}
                  />
                  <Route
                    path="/FastEvalGroupListView"
                    component={waitFor(FastEvalGroupListView)}
                  />
                  <Route
                    path="/IndividualFastEvalListView"
                    component={waitFor(IndividualFastEvalListView)}
                  />
                  <Route
                    path="/FastGroupManagementListView"
                    component={waitFor(FastGroupManagementListView)}
                  />
                  <Route
                    path="/FastEvalGroupComparisonView"
                    component={waitFor(FastEvalGroupComparisonView)}
                  />
                  <Route
                    path="/FastEvaluationView"
                    component={waitFor(FastEvaluationView)}
                  />
                  <Route
                    path="/GroupGalleryView"
                    component={waitFor(GroupGalleryView)}
                  />
                  <Route
                    path="/AdvancedGroupManagementView"
                    component={waitForEntity(AdvancedGroupManagementView, ["groupId"])}
                  />
                  <Route
                    path="/GridManagementView"
                    component={waitFor(GridManagementView)}
                  />
                  <Route
                    path="/fast-eval/grids"
                    component={waitFor(GridManagementView)}
                  />
                  <Route
                    path="/GridDetailView/:gridId"
                    component={waitForEntity(GridDetailView, ["gridId"])}
                  />
                  <Route
                    path="/GridBuilderView/:gridId"
                    component={waitFor(GridBuilderView)}
                  />
                  <Route
                    path="/GridBuilderView"
                    component={waitFor(GridBuilderView)}
                  />
                  <Route
                    path="/fast-eval/import"
                    component={waitFor(GridImportView)}
                  />
                  <Route
                    path="/GroupActionsView"
                    component={waitFor(GroupActionsView)}
                  />
                  <Route
                    path="/ObjectivesListView"
                    component={waitFor(ObjectivesListView)}
                  />
                  <Route
                    path="/HelpCenterView"
                    component={waitFor(HelpCenterView)}
                  />
                  <Route
                    path="/SupportTicketsView"
                    component={waitFor(SupportTicketsView)}
                  />
                  <Route
                    path="/AdminTicketsView"
                    component={waitFor(AdminTicketsView)}
                  />
                  <Route
                    path="/DocumentListView"
                    component={waitFor(DocumentListView)}
                  />
                  <Route
                    path="/RafikiTranslationDebug"
                    component={waitFor(RafikiTranslationDebugView)}
                  />
                  <Route
                    path="/EthogramTranslationDebug"
                    component={waitFor(EthogramTranslationDebugView)}
                  />
                  <Route
                    path="/ContextTranslationDebug"
                    component={waitFor(ContextTranslationDebugView)}
                  />
                  <Route
                    path="/DeeplStats"
                    component={waitFor(DeeplStatsView)}
                  />
                  <Route
                    path="/RafikiStats"
                    component={waitFor(RafikiStatsView)}
                  />
                  <Route
                    path="/DeferredObsDebug"
                    component={waitFor(DeferredObsDebug)}
                  />
                  <Route
                    path="/MissingEffectiveTimestamp"
                    component={waitFor(MissingEffectiveTimestampView)}
                  />
                  <Route exact path="/" component={DefaultRedirect} />
                  <Route component={DefaultRedirect} />
                </Switch>
                </LegacyRouteGuard>
              </Suspense>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </Base>
      </LegacyEntryGate>
      </LegacyAuthenticationGate>
    );
  }
};

export default withRouter(Routes);
